import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserTableRow = ({ user, onEdit, onDelete, onToggleStatus, language, direction }) => {
  const [showActions, setShowActions] = useState(false);

  const getRoleLabel = (role) => {
    const roleLabels = {
      administrator: {
        en: 'Administrator',
        fr: 'Administrateur',
        ar: 'مدير'
      },
      lawyer: {
        en: 'Lawyer',
        fr: 'Avocat',
        ar: 'محامي'
      },
      secretary: {
        en: 'Secretary',
        fr: 'Secrétaire',
        ar: 'سكرتير'
      }
    };
    return roleLabels?.[role]?.[language] || role;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      active: {
        en: 'Active',
        fr: 'Actif',
        ar: 'نشط'
      },
      inactive: {
        en: 'Inactive',
        fr: 'Inactif',
        ar: 'غير نشط'
      },
      suspended: {
        en: 'Suspended',
        fr: 'Suspendu',
        ar: 'معلق'
      }
    };
    return statusLabels?.[status]?.[language] || status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (language === 'ar') {
      return date?.toLocaleDateString('ar-MA');
    } else if (language === 'fr') {
      return date?.toLocaleDateString('fr-FR');
    }
    return date?.toLocaleDateString('en-US');
  };

  const editLabel = {
    en: 'Edit',
    fr: 'Modifier',
    ar: 'تعديل'
  };

  const deleteLabel = {
    en: 'Delete',
    fr: 'Supprimer',
    ar: 'حذف'
  };

  const toggleStatusLabel = {
    en: user?.status === 'active' ? 'Deactivate' : 'Activate',
    fr: user?.status === 'active' ? 'Désactiver' : 'Activer',
    ar: user?.status === 'active' ? 'تعطيل' : 'تفعيل'
  };

  return (
    <>
      <tr className="hidden lg:table-row border-b border-border hover:bg-muted/50 transition-colors">
        <td className="px-4 md:px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
              {user?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </td>
        <td className="px-4 md:px-6 py-4">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
            user?.role === 'administrator' ? 'bg-error/10 text-error' :
            user?.role === 'lawyer'? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'
          }`}>
            <Icon 
              name={user?.role === 'administrator' ? 'Shield' : user?.role === 'lawyer' ? 'Briefcase' : 'User'} 
              size={14} 
            />
            {getRoleLabel(user?.role)}
          </span>
        </td>
        <td className="px-4 md:px-6 py-4">
          <div className="flex flex-wrap gap-1">
            {user?.permissions?.slice(0, 3)?.map((permission, index) => (
              <span key={index} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                {permission}
              </span>
            ))}
            {user?.permissions?.length > 3 && (
              <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                +{user?.permissions?.length - 3}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 md:px-6 py-4">
          <p className="text-sm text-foreground">{formatDate(user?.lastLogin)}</p>
        </td>
        <td className="px-4 md:px-6 py-4">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
            user?.status === 'active' ? 'bg-success/10 text-success' :
            user?.status === 'suspended'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              user?.status === 'active' ? 'bg-success' :
              user?.status === 'suspended'? 'bg-warning' : 'bg-muted-foreground'
            }`} />
            {getStatusLabel(user?.status)}
          </span>
        </td>
        <td className="px-4 md:px-6 py-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              iconName="Edit"
              iconSize={16}
              onClick={() => onEdit(user)}
              aria-label={editLabel?.[language]}
            />
            <Button
              variant="ghost"
              size="icon"
              iconName={user?.status === 'active' ? 'UserX' : 'UserCheck'}
              iconSize={16}
              onClick={() => onToggleStatus(user)}
              aria-label={toggleStatusLabel?.[language]}
            />
            <Button
              variant="ghost"
              size="icon"
              iconName="Trash2"
              iconSize={16}
              onClick={() => onDelete(user)}
              aria-label={deleteLabel?.[language]}
            />
          </div>
        </td>
      </tr>
      <div className="lg:hidden bg-card border border-border rounded-lg p-4 mb-3">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
              {user?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="MoreVertical" size={18} />
          </button>
        </div>

        <div className="space-y-2 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {language === 'en' ? 'Role' : language === 'fr' ? 'Rôle' : 'الدور'}
            </span>
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
              user?.role === 'administrator' ? 'bg-error/10 text-error' :
              user?.role === 'lawyer'? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'
            }`}>
              <Icon 
                name={user?.role === 'administrator' ? 'Shield' : user?.role === 'lawyer' ? 'Briefcase' : 'User'} 
                size={12} 
              />
              {getRoleLabel(user?.role)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {language === 'en' ? 'Status' : language === 'fr' ? 'Statut' : 'الحالة'}
            </span>
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
              user?.status === 'active' ? 'bg-success/10 text-success' :
              user?.status === 'suspended'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                user?.status === 'active' ? 'bg-success' :
                user?.status === 'suspended'? 'bg-warning' : 'bg-muted-foreground'
              }`} />
              {getStatusLabel(user?.status)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {language === 'en' ? 'Last Login' : language === 'fr' ? 'Dernière connexion' : 'آخر تسجيل دخول'}
            </span>
            <span className="text-xs text-foreground">{formatDate(user?.lastLogin)}</span>
          </div>
        </div>

        {showActions && (
          <div className="flex flex-col gap-2 pt-3 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              fullWidth
              onClick={() => {
                onEdit(user);
                setShowActions(false);
              }}
            >
              {editLabel?.[language]}
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName={user?.status === 'active' ? 'UserX' : 'UserCheck'}
              iconPosition="left"
              fullWidth
              onClick={() => {
                onToggleStatus(user);
                setShowActions(false);
              }}
            >
              {toggleStatusLabel?.[language]}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              fullWidth
              onClick={() => {
                onDelete(user);
                setShowActions(false);
              }}
            >
              {deleteLabel?.[language]}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default UserTableRow;